import { useCallback } from 'react';

import { getContext } from 'configs/context';

import { BATCH_MINIAPP_ID } from 'pages/Batch/configs';
import analytics from 'pages/Batch/utils/analytics';

import type { Credits } from '@picsart/miniapp-sdk-types';

import { PUBLISH_EVENT, isDev } from 'configs/constants';

import useAuthInfo from 'hooks/useAuthInfo';

import isUserAuthorized from 'utils/common/isUserAuthorized';

type UseSubscribeReturnType = {
  checkSubscriptionLimits: (creditsData: Credits, toolPrice: number) => Promise<boolean | undefined>;
  subscribe: (isStorageFull?: boolean) => Promise<void>;
  showOfferScreen: (event: PUBLISH_EVENT) => Promise<void>;
};

const useSubscribe = (): UseSubscribeReturnType => {
  const { isProOrGoldUser } = useAuthInfo();

  const showOfferScreen = useCallback(async (event: PUBLISH_EVENT) => {
    const data =
      event === PUBLISH_EVENT.REGISTRATION
        ? {
            source: BATCH_MINIAPP_ID,
            action: BATCH_MINIAPP_ID,
          }
        : {
            source: BATCH_MINIAPP_ID,
            action: BATCH_MINIAPP_ID,
            touchpoint: BATCH_MINIAPP_ID,
            is_pro_touchpoint: true,
            toolId: BATCH_MINIAPP_ID,
            screen_id: BATCH_MINIAPP_ID,
            forceTier: 'pro',
          };
    const payload = { event, data };
    try {
      await getContext().handlers.publishEvent(payload);
    } catch (err) {
      console.error('[offerFlow] showOfferScreen ← publishEvent failed for', event, err);
      throw err;
    }
  }, []);

  const subscribe = useCallback(
    async (isStorageFull?: boolean) => {
      const userAuthorized = isUserAuthorized();

      if (!userAuthorized) {
        await showOfferScreen(PUBLISH_EVENT.REGISTRATION);
        analytics.registrationOpen();
        return;
      }

      if (isProOrGoldUser && !isStorageFull) {
        return;
      }

      await showOfferScreen(PUBLISH_EVENT.OFFER);
      analytics.subscriptionOfferOpen();
    },
    [isProOrGoldUser, showOfferScreen],
  );

  const checkSubscriptionLimits = useCallback(
    async (creditsData: Credits, toolPrice: number) => {
      const { areCreditsAvailable, creditsInfo } = creditsData;
      const userAuthorized = isUserAuthorized();

      if (isDev) {
        return true;
      }

      if (!userAuthorized) {
        await subscribe();
        return false;
      }

      if (!areCreditsAvailable) {
        await showOfferScreen(PUBLISH_EVENT.OFFER);
        return false;
      }

      if (creditsInfo) {
        const { credits, addonCredits } = creditsInfo;
        const availableCredits = credits + addonCredits;

        if (availableCredits >= Number(toolPrice)) {
          return true;
        }

        await showOfferScreen(PUBLISH_EVENT.OFFER);
        return false;
      }

      return true;
    },
    [showOfferScreen, subscribe],
  );

  return {
    checkSubscriptionLimits,
    subscribe,
    showOfferScreen,
  };
};

export default useSubscribe;
